import { service } from '@ember/service'
import Component from '@glimmer/component'
import { t, type IntlService } from 'ember-intl'
import type { UIScopeName } from 'teacher-ui-client/reporting/ui-scope/route'
import type { AxisLabelsFormatterContextObject, Point, PointOptionsObject, XAxisOptions } from 'highcharts'
import { fullGradeName } from 'teacher-ui-client/helpers/full-grade-name'
import { colors } from '@colors'
import type { AveragesReportDataForGrade } from 'teacher-ui-client/components/reporting/writing-legends/quizzes-completed/averages/report-loader'
import { maxBy } from 'lodash'
import { LeftSplitChart } from 'teacher-ui-client/components/giraffe/left-split-chart'
import { SeriesBar } from 'teacher-ui-client/components/giraffe/primitives/series-bar'
import { SeriesColumn, type SeriesColumnOptions } from 'teacher-ui-client/components/giraffe/primitives/series-column'
import { array } from '@ember/helper'
import { SummaryBox } from 'teacher-ui-client/components/giraffe/summary-box'
import { hash } from '@ember/helper'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'

interface PointLessonData {
  lesson: { average_score: number; student_count: number; attempts: number }
}

interface Signature {
  Args: {
    reportData: AveragesReportDataForGrade
    scopeName: UIScopeName
  }
}

export class AveragesReport extends Component<Signature> {
  @service intl!: IntlService

  get lessons(): AveragesReportDataForGrade['lessons'] {
    return this.args.reportData.lessons
  }

  get scoreRangesDesc() {
    return this.args.reportData.summary.scores
      ? [
          // replace student_count 0 values w/null to hide bar(s) rather than
          // render 1px wide bars
          { range: '80-100', student_count: this.args.reportData.summary.scores?.['80-100'] || null },
          { range: '50-79', student_count: this.args.reportData.summary.scores?.['50-79'] || null },
          { range: '0-49', student_count: this.args.reportData.summary.scores?.['0-49'] || null },
        ]
      : []
  }

  get scoreRangesMaxStudentCount() {
    return maxBy(this.scoreRangesDesc, (item) => item.student_count)?.student_count ?? 0
  }

  get summaryChartData() {
    const data = this.scoreRangesDesc.map((score, index) => {
      return { x: index, y: score.student_count, score }
    })
    return [{ name: 'student_counts', data, type: 'bar' as const }]
  }

  get summaryChartCategories() {
    return this.scoreRangesDesc.map(({ range }) => range)
  }

  get summaryChartColors() {
    return [colors.msGreen300, colors.juicyOrange300, colors.watermelonyRed300]
  }

  get summaryBoxTitle() {
    const title = this.intl.t('reporting.statBoxes.assessmentScoresStudent.average')
    if (typeof this.args.reportData.summary.average_score === 'number') {
      return `${title}: ${this.args.reportData.summary.average_score}%`
    }
    return title
  }

  get summaryChartXAxis(): XAxisOptions {
    return {
      title: {
        text: `${this.intl.t('scores')} %`,
        align: 'high',
        rotation: 0,
        offset: -5,
        y: -7,
      },
      lineColor: colors.dustyBlack200,
      tickWidth: 1,
      tickColor: colors.dustyBlack200,
      categories: this.summaryChartCategories,
      labels: {
        x: -7,
      },
    }
  }

  get seriesColumnData(): SeriesColumnOptions[] {
    const data = this.lessons.map((lesson, index): PointOptionsObject & PointLessonData => {
      return { x: index, y: lesson.average_score, lesson, color: this.columnColourForLesson(lesson) }
    })
    return [{ name: 'average_scores', data, type: 'column', maxPointWidth: 40 }]
  }

  columnColourForLesson(lesson: { average_score: number }) {
    const { average_score } = lesson
    if (average_score >= 80) {
      return colors.msGreen300
    } else if (average_score >= 50) {
      return colors.juicyOrange300
    } else {
      return colors.watermelonyRed300
    }
  }

  get seriesColumnCategories() {
    return this.lessons.map(({ lesson }) => String(lesson))
  }

  yAxisLabelFormatter(this: AxisLabelsFormatterContextObject) {
    return `${this.value}%`
  }

  get toolTipFormatter() {
    const studentCountLabel = this.intl.t('reporting.statBoxes.assessmentScoresTotals.studentCount')
    return function (this: Point) {
      const { lesson } = this as Point & PointLessonData
      const { attempts, average_score: score, student_count: studentCount } = lesson
      return `Attempts: ${attempts}<br>Score: ${score}%<br>${studentCountLabel}: ${studentCount}`
    }
  }

  <template>
    <LeftSplitChart>
      <:left>
        <SummaryBox
          class="h-full"
          @title={{this.summaryBoxTitle}}
          @grade={{fullGradeName @reportData.grade_position}}
          @colorScheme={{SummaryBoxColorScheme.WritingLegendsBlue}}
        >
          <SeriesBar
            @chartSpacing={{array 25 10 10 0}}
            @data={{this.summaryChartData}}
            @colors={{this.summaryChartColors}}
            @colorByPoint={{true}}
            @plotBackgroundColor="transparent"
            @yRange={{array 0 this.scoreRangesMaxStudentCount}}
            @yAxisLabel={{t "reporting.statBoxes.assessmentScoresTotals.numOfStudents"}}
            @xAxis={{this.summaryChartXAxis}}
          />
        </SummaryBox>
      </:left>
      <:right>
        <SeriesColumn
          @data={{this.seriesColumnData}}
          @categories={{this.seriesColumnCategories}}
          @title={{hash text=(t "reporting.chartTitles.quizScoresAverages")}}
          @toolTipFormatter={{this.toolTipFormatter}}
          @yAxisLabelFormatter={{this.yAxisLabelFormatter}}
          @chartSpacing={{array 15 10 15 25}}
          @xAxisLabel={{t "lesson"}}
          @yAxisLabel={{t "score"}}
          @yRange={{array 0 100}}
        />
      </:right>
    </LeftSplitChart>
  </template>
}
